import React, { useState, useEffect } from 'react';
import { Sparkles, Expand, Calendar, User, Settings, Plus, Trash2, Loader, Database, Zap } from 'lucide-react';
import { Input } from '../../ui/Input';
import { Textarea } from '../../ui/Textarea';
import { Dropdown } from '../../ui/Dropdown';
import { Toggle } from '../../ui/Toggle';
import { Button } from '../../ui/Button';
import { FormSection } from '../../ui/FormSection';
import { Accordion } from '../../ui/Accordion';
import { ToolCategory, Tool } from '../../ui/ToolCategory';
import { IntegrationModal, IntegrationData } from '../../ui/IntegrationModal';
import { FileUpload, UploadedFile } from '../../ui/FileUpload';
import { IntegrationApps } from '../../ui/IntegrationApps';
import { SkillSelector } from '../../skills/SkillSelector';
import {
  JiraIcon,
  ConfluenceIcon,
  GitHubIcon,
  AzureDevOpsIcon,
  SlackIcon,
  TeamsIcon,
} from '../../common/BrandIcons';
import api, { AIModel, Datasource, IntegrationSummary } from '../../../services/api';
import styles from './CreateAssistantForm.module.css';

interface CreateAssistantFormProps {
  onCancel: () => void;
  onSave: (data: AssistantFormData) => void;
  isSubmitting?: boolean;
}

export interface AssistantFormData {
  name: string;
  slug: string;
  iconUrl: string;
  description: string;
  categories: string[];
  conversationStarters: string[];
  systemInstructions: string;
  promptVariables: string[];
  llmModel: string;
  temperature: number;
  topP: number;
  project: string;
  sharedWithProject: boolean;
  datasourceContext: string[];
  subAssistants: string[];
  selectedTools: Record<string, string[]>;
  externalTools: string[];
  mcpServers: string[];
  // Uploaded files for new datasource
  uploadedFiles: UploadedFile[];
  // Selected existing datasources
  selectedDatasources: string[];
  // Selected integrations
  selectedIntegrations: string[];
  // Selected skills
  selectedSkills?: string[];
}

const projectOptions = [
  { value: 'umapathi@eclatansys.com', label: 'umapathi@eclatansys.com' },
  { value: 'bringiq', label: 'bringiq' },
  { value: 'demo', label: 'demo' },
];

const categoryOptions = [
  { value: 'development', label: 'Development' },
  { value: 'testing', label: 'Testing' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'devops', label: 'DevOps' },
  { value: 'data', label: 'Data' },
  { value: 'analytics', label: 'Analytics' },
];

// LLM models will be fetched from API - these are fallback options
const fallbackModelOptions = [
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
];

const datasourceOptions = [
  { value: 'confluence', label: 'Confluence Wiki' },
  { value: 'github', label: 'GitHub Repositories' },
  { value: 'jira', label: 'Jira Issues' },
  { value: 'notion', label: 'Notion Pages' },
];

const subAssistantOptions = [
  { value: 'code-reviewer', label: 'Code Reviewer' },
  { value: 'test-writer', label: 'Test Writer' },
  { value: 'doc-generator', label: 'Documentation Generator' },
];

// Tool categories data
const toolCategories: { name: string; tools: Tool[] }[] = [
  {
    name: 'Access Management',
    tools: [
      { id: 'keycloak', name: 'Keycloak' },
      { id: 'okta', name: 'Okta' },
      { id: 'auth0', name: 'Auth0' },
    ],
  },
  {
    name: 'Azure DevOps Test Plan',
    tools: [
      { id: 'ado-testplan-create', name: 'Create Test Plan' },
      { id: 'ado-testplan-run', name: 'Run Test Plan' },
      { id: 'ado-testplan-results', name: 'Get Test Results' },
    ],
  },
  {
    name: 'Azure DevOps Wiki',
    tools: [
      { id: 'ado-wiki-read', name: 'Read Wiki Page' },
      { id: 'ado-wiki-create', name: 'Create Wiki Page' },
      { id: 'ado-wiki-update', name: 'Update Wiki Page' },
    ],
  },
  {
    name: 'Azure DevOps Work Item',
    tools: [
      { id: 'ado-wi-create', name: 'Create Work Item' },
      { id: 'ado-wi-update', name: 'Update Work Item' },
      { id: 'ado-wi-query', name: 'Query Work Items' },
    ],
  },
  {
    name: 'Cloud',
    tools: [
      { id: 'aws-s3', name: 'AWS S3' },
      { id: 'azure-blob', name: 'Azure Blob Storage' },
      { id: 'gcp-storage', name: 'GCP Cloud Storage' },
    ],
  },
  {
    name: 'Codebase Tools',
    tools: [
      { id: 'code-search', name: 'Code Search' },
      { id: 'code-analysis', name: 'Code Analysis' },
      { id: 'code-review', name: 'Code Review' },
    ],
  },
  {
    name: 'Project Management',
    tools: [
      { id: 'jira', name: 'Jira' },
      { id: 'asana', name: 'Asana' },
      { id: 'monday', name: 'Monday.com' },
    ],
  },
  {
    name: 'VCS',
    tools: [
      { id: 'github', name: 'GitHub' },
      { id: 'gitlab', name: 'GitLab' },
      { id: 'bitbucket', name: 'Bitbucket' },
    ],
  },
  {
    name: 'Data Management',
    tools: [
      { id: 'postgres', name: 'PostgreSQL' },
      { id: 'mongodb', name: 'MongoDB' },
      { id: 'redis', name: 'Redis' },
    ],
  },
  {
    name: 'IT Service Management',
    tools: [
      { id: 'servicenow', name: 'ServiceNow' },
      { id: 'zendesk', name: 'Zendesk' },
      { id: 'freshdesk', name: 'Freshdesk' },
    ],
  },
  {
    name: 'Notification',
    tools: [
      { id: 'slack', name: 'Slack' },
      { id: 'teams', name: 'Microsoft Teams' },
      { id: 'email', name: 'Email' },
    ],
  },
  {
    name: 'OpenAPI',
    tools: [
      { id: 'openapi-spec', name: 'OpenAPI Spec Parser' },
      { id: 'api-client', name: 'API Client Generator' },
    ],
  },
  {
    name: 'Quality Assurance',
    tools: [
      { id: 'sonarqube', name: 'SonarQube' },
      { id: 'codeclimate', name: 'CodeClimate' },
    ],
  },
  {
    name: 'Report Portal',
    tools: [
      { id: 'reportportal-launch', name: 'Launch Report' },
      { id: 'reportportal-results', name: 'Get Results' },
    ],
  },
  {
    name: 'Git',
    tools: [
      { id: 'git-clone', name: 'Clone Repository' },
      { id: 'git-commit', name: 'Commit Changes' },
      { id: 'git-pr', name: 'Create Pull Request' },
    ],
  },
  {
    name: 'Research',
    tools: [
      { id: 'web-search', name: 'Web Search' },
      { id: 'doc-search', name: 'Documentation Search' },
    ],
  },
  {
    name: 'FileSystem',
    tools: [
      { id: 'file-read', name: 'Read File' },
      { id: 'file-write', name: 'Write File' },
      { id: 'file-list', name: 'List Files' },
    ],
  },
  {
    name: 'Plugin',
    tools: [
      { id: 'custom-plugin', name: 'Custom Plugin' },
    ],
  },
  {
    name: 'Platform Tools',
    tools: [
      { id: 'platform-deploy', name: 'Deploy' },
      { id: 'platform-monitor', name: 'Monitor' },
    ],
  },
];

const initialFormData: AssistantFormData = {
  name: '',
  slug: '',
  iconUrl: '',
  description: '',
  categories: [],
  conversationStarters: [],
  systemInstructions: '',
  promptVariables: [],
  llmModel: 'gpt-4o',
  temperature: 0.7,
  topP: 1,
  project: '',
  sharedWithProject: false,
  datasourceContext: [],
  subAssistants: [],
  selectedTools: {},
  externalTools: [],
  mcpServers: [],
  uploadedFiles: [],
  selectedDatasources: [],
  selectedIntegrations: [],
  selectedSkills: [],
};

export const CreateAssistantForm: React.FC<CreateAssistantFormProps> = ({
  onCancel,
  onSave,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<AssistantFormData>(initialFormData);
  const [integrationModal, setIntegrationModal] = useState<{
    isOpen: boolean;
    categoryName: string;
  }>({ isOpen: false, categoryName: '' });

  // State for LLM models from API
  const [llmModels, setLlmModels] = useState<AIModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(true);
  
  // State for existing datasources
  const [availableDatasources, setAvailableDatasources] = useState<Datasource[]>([]);
  const [loadingDatasources, setLoadingDatasources] = useState(true);
  
  // State for connected integrations
  const [connectedIntegrations, setConnectedIntegrations] = useState<IntegrationSummary[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);

  // State for AI enhancement
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState<string | null>(null);

  // Fetch LLM models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoadingModels(true);
        const response = await api.getAIModels();
        if (response.success && response.data?.models) {
          setLlmModels(response.data.models);
          // Set default model if available
          if (response.data.models.length > 0 && !formData.llmModel) {
            setFormData(prev => ({
              ...prev,
              llmModel: response.data.models[0].id,
            }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch LLM models:', error);
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
  }, []);

  // Fetch available datasources
  useEffect(() => {
    const fetchDatasources = async () => {
      try {
        setLoadingDatasources(true);
        const response = await api.listAllDatasources();
        if (response.success && response.data) {
          const datasourceData = Array.isArray(response.data) ? response.data : [];
          setAvailableDatasources(datasourceData);
        }
      } catch (error) {
        console.error('Failed to fetch datasources:', error);
        setAvailableDatasources([]);
      } finally {
        setLoadingDatasources(false);
      }
    };
    fetchDatasources();
  }, []);

  // Fetch connected integrations
  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        setLoadingIntegrations(true);
        const response = await api.getConnectedIntegrations();
        if (response.success && response.data) {
          const integrationData = Array.isArray(response.data) ? response.data : [];
          setConnectedIntegrations(integrationData.filter(i => i.connected));
        }
      } catch (error) {
        console.error('Failed to fetch integrations:', error);
        setConnectedIntegrations([]);
      } finally {
        setLoadingIntegrations(false);
      }
    };
    fetchIntegrations();
  }, []);

  // Generate model options from API or fallback
  const llmModelOptions = llmModels.length > 0
    ? llmModels.map(model => ({
        value: model.id,
        label: `${model.name} (${model.publisher})`,
      }))
    : fallbackModelOptions;

  const handleFieldChange = <K extends keyof AssistantFormData>(
    field: K,
    value: AssistantFormData[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleToolChange = (category: string, toolId: string, checked: boolean) => {
    setFormData((prev) => {
      const categoryTools = prev.selectedTools[category] || [];
      const newTools = checked
        ? [...categoryTools, toolId]
        : categoryTools.filter((id) => id !== toolId);
      
      return {
        ...prev,
        selectedTools: {
          ...prev.selectedTools,
          [category]: newTools,
        },
      };
    });
  };

  const handleSelectAllTools = (category: string, tools: Tool[], checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      selectedTools: {
        ...prev.selectedTools,
        [category]: checked ? tools.map((t) => t.id) : [],
      },
    }));
  };

  const handleAddConversationStarter = (starter: string) => {
    setFormData((prev) => ({
      ...prev,
      conversationStarters: [...prev.conversationStarters, starter],
    }));
  };

  const handleRemoveConversationStarter = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      conversationStarters: prev.conversationStarters.filter((_, i) => i !== index),
    }));
  };

  const handleGenerateWithAI = async () => {
    if (!formData.systemInstructions.trim()) {
      setEnhanceError('Please enter some initial instructions to enhance');
      return;
    }

    setIsEnhancing(true);
    setEnhanceError(null);

    try {
      const response = await api.enhanceWithAI(formData.systemInstructions, {
        type: 'system_instructions',
        assistantName: formData.name,
        assistantDescription: formData.description,
      });

      if (response.success && response.data?.enhancedText) {
        setFormData(prev => ({
          ...prev,
          systemInstructions: response.data.enhancedText,
        }));
      } else {
        setEnhanceError('Failed to enhance text. Please try again.');
      }
    } catch (err: any) {
      setEnhanceError(err.message || 'Failed to generate enhanced instructions');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleExpandInstructions = () => {
    // Placeholder for expanding instructions
    console.log('Expand instructions clicked');
  };

  const handleInsertCurrentUser = () => {
    setFormData((prev) => ({
      ...prev,
      systemInstructions: prev.systemInstructions + ' {{currentUser}}',
    }));
  };

  const handleInsertDate = () => {
    setFormData((prev) => ({
      ...prev,
      systemInstructions: prev.systemInstructions + ' {{currentDate}}',
    }));
  };

  const handleManagePromptVars = () => {
    console.log('Manage prompt vars clicked');
  };

  const handleAddIntegration = (categoryName: string) => {
    setIntegrationModal({ isOpen: true, categoryName });
  };

  const handleSaveIntegration = (integration: IntegrationData) => {
    console.log('Integration saved:', integration);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    handleFieldChange('name', name);
    if (!formData.slug || formData.slug === generateSlug(formData.name)) {
      handleFieldChange('slug', generateSlug(name));
    }
  };

  const getTotalSelectedTools = () => {
    return Object.values(formData.selectedTools).flat().length;
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} id="create-assistant-form">
      <div className={styles.content}>
        {/* Assistant Setup */}
        <FormSection title="Assistant Setup">
          <div className={styles.projectRow}>
            <Dropdown
              label="Project name:"
              options={projectOptions}
              value={formData.project}
              onChange={(value) => handleFieldChange('project', value)}
              placeholder="Select project"
            />
            <div className={styles.toggleWrapper}>
              <Toggle
                checked={formData.sharedWithProject}
                onChange={(checked) => handleFieldChange('sharedWithProject', checked)}
              />
              <span className={styles.toggleLabel}>Shared with<br />project</span>
            </div>
          </div>

          <div className={styles.importantNote}>
            <span className={styles.noteIcon}>ⓘ</span>
            <span>Important note: You can't share an assistant that has personal integrations on one of it's tools.</span>
          </div>

          <Input
            label="Name:"
            value={formData.name}
            onChange={handleNameChange}
            placeholder="Name*"
            required
          />

          <div className={styles.slugField}>
            <span className={styles.slugLabel}>Slug:</span>
            <input
              type="text"
              className={styles.slugInput}
              value={formData.slug}
              onChange={(e) => handleFieldChange('slug', e.target.value)}
              placeholder="Unique human-readable identifier"
            />
          </div>

          <Input
            label="Icon URL:"
            value={formData.iconUrl}
            onChange={(e) => handleFieldChange('iconUrl', e.target.value)}
            placeholder="URL to the assistant's icon"
          />

          <Textarea
            label="Description:"
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder="Description*"
            rows={4}
          />

          <div className={styles.categoriesField}>
            <Dropdown
              label="Categories:"
              options={categoryOptions}
              value={formData.categories[0] || ''}
              onChange={(value) => handleFieldChange('categories', [value])}
              placeholder="Select categories"
            />
            <span className={styles.helperText}>
              <span className={styles.infoIcon}>ⓘ</span>
              Choose up to 3 categories that describe your assistant's use case.
            </span>
          </div>

          <div className={styles.conversationStarters}>
            <div className={styles.conversationStartersHeader}>
              <label className={styles.label}>
                Conversation starters <span className={styles.infoIcon}>ⓘ</span>
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<Plus size={14} />}
                onClick={() => handleAddConversationStarter('')}
              >
                Add
              </Button>
            </div>
            {formData.conversationStarters.map((starter, index) => (
              <div key={index} className={styles.starterRow}>
                <Input
                  value={starter}
                  onChange={(e) => {
                    const newStarters = [...formData.conversationStarters];
                    newStarters[index] = e.target.value;
                    handleFieldChange('conversationStarters', newStarters);
                  }}
                  placeholder="Enter conversation starter"
                />
                <button
                  type="button"
                  className={styles.deleteButton}
                  onClick={() => handleRemoveConversationStarter(index)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {formData.conversationStarters.length === 0 && (
              <div className={styles.starterRow}>
                <Input placeholder="Enter conversation starter" disabled />
                <button type="button" className={styles.deleteButton} disabled>
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
        </FormSection>

        {/* Behavior & Logic */}
        <FormSection title="Behavior & Logic" subtitle="Control how your assistant responds, reasons, and communicates.">
          <div className={styles.instructionsHeader}>
            <label className={styles.label}>System Instructions</label>
            <div className={styles.instructionsActions}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={isEnhancing ? <Loader size={14} className={styles.spinning} /> : <Sparkles size={14} />}
                onClick={handleGenerateWithAI}
                disabled={isEnhancing || !formData.systemInstructions.trim()}
              >
                {isEnhancing ? 'Enhancing...' : 'Generate with AI'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<Expand size={14} />}
                onClick={handleExpandInstructions}
              >
                Expand
              </Button>
            </div>
          </div>

          {enhanceError && (
            <div className={styles.errorText}>{enhanceError}</div>
          )}

          <div className={styles.variableButtons}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<User size={14} />}
              onClick={handleInsertCurrentUser}
            >
              Current User
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<Calendar size={14} />}
              onClick={handleInsertDate}
            >
              Date
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<Settings size={14} />}
              onClick={handleManagePromptVars}
            >
              Manage Prompt Vars
            </Button>
          </div>

          <Textarea
            value={formData.systemInstructions}
            onChange={(e) => handleFieldChange('systemInstructions', e.target.value)}
            placeholder="Enter system instructions for the assistant..."
            rows={6}
          />

          <div className={styles.row}>
            <Dropdown
              label={loadingModels ? 'LLM Model (Loading...)' : 'LLM Model'}
              options={llmModelOptions}
              value={formData.llmModel}
              onChange={(value) => handleFieldChange('llmModel', value)}
              disabled={loadingModels || isSubmitting}
            />
          </div>

          <div className={styles.row}>
            <Input
              label="Temperature"
              type="number"
              value={formData.temperature.toString()}
              onChange={(e) => handleFieldChange('temperature', parseFloat(e.target.value) || 0)}
              min={0}
              max={2}
              step={0.1}
              helperText="0-2"
            />
            <Input
              label="Top P"
              type="number"
              value={formData.topP.toString()}
              onChange={(e) => handleFieldChange('topP', parseFloat(e.target.value) || 0)}
              min={0}
              max={1}
              step={0.1}
              helperText="0-1"
            />
          </div>
        </FormSection>

        {/* Context & Data Sources */}
        <FormSection title="Context & Data Sources" subtitle="Select datasources and integrations for your assistant's knowledge.">
          {/* Existing Datasources Selection */}
          <div className={styles.datasourceSelection}>
            <label className={styles.label}>
              <Database size={16} />
              Select Datasources
              <span className={styles.infoIcon}>ⓘ</span>
            </label>
            <p className={styles.helperText}>
              Choose from your existing datasources. Go to Datasources page to create new ones.
            </p>
            {loadingDatasources ? (
              <div className={styles.loadingState}>Loading datasources...</div>
            ) : availableDatasources.length === 0 ? (
              <div className={styles.emptyState}>
                No datasources available. <a href="/datasources">Create one</a>
              </div>
            ) : (
              <div className={styles.checkboxGrid}>
                {availableDatasources.map(ds => (
                  <label key={ds.id} className={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={formData.selectedDatasources.includes(ds.id)}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...formData.selectedDatasources, ds.id]
                          : formData.selectedDatasources.filter(id => id !== ds.id);
                        handleFieldChange('selectedDatasources', updated);
                      }}
                      disabled={isSubmitting}
                    />
                    <div className={styles.checkboxContent}>
                      <span className={styles.checkboxLabel}>{ds.name}</span>
                      <span className={styles.checkboxMeta}>
                        {ds.status === 'complete' ? '✓ Ready' : ds.status}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Connected Integrations Selection */}
          <div className={styles.integrationSelection}>
            <label className={styles.label}>
              <Zap size={16} />
              Select Integrations
              <span className={styles.infoIcon}>ⓘ</span>
            </label>
            <p className={styles.helperText}>
              Choose which connected integrations to use. Go to Integrations page to connect more.
            </p>
            {loadingIntegrations ? (
              <div className={styles.loadingState}>Loading integrations...</div>
            ) : connectedIntegrations.length === 0 ? (
              <div className={styles.emptyState}>
                No integrations connected. <a href="/integrations">Connect one</a>
              </div>
            ) : (
              <div className={styles.checkboxGrid}>
                {connectedIntegrations.map(integration => (
                  <label key={integration.provider} className={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={formData.selectedIntegrations.includes(integration.provider)}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...formData.selectedIntegrations, integration.provider]
                          : formData.selectedIntegrations.filter(p => p !== integration.provider);
                        handleFieldChange('selectedIntegrations', updated);
                      }}
                      disabled={isSubmitting}
                    />
                    <div className={styles.checkboxContent}>
                      <span className={styles.checkboxLabel} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {integration.provider === 'jira' && <JiraIcon size={16} />}
                        {integration.provider === 'confluence' && <ConfluenceIcon size={16} />}
                        {integration.provider === 'github' && <GitHubIcon size={16} />}
                        {integration.provider === 'slack' && <SlackIcon size={16} />}
                        {integration.provider === 'azure-devops' && <AzureDevOpsIcon size={16} />}
                        {integration.provider === 'teams' && <TeamsIcon size={16} />}
                        {integration.displayName || integration.provider}
                      </span>
                      <span className={styles.checkboxMeta}>
                        {integration.accountInfo?.name || integration.accountInfo?.email || 'Connected'}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className={styles.divider} />

          {/* Upload New Files */}
          <FileUpload
            files={formData.uploadedFiles}
            onFilesChange={(files) => handleFieldChange('uploadedFiles', files)}
            label="Upload Additional Documents"
            helperText="Upload new files to add to this assistant's knowledge base."
            maxFiles={20}
            maxSize={50}
            disabled={isSubmitting}
          />

          <Dropdown
            label="Sub-Assistants"
            options={subAssistantOptions}
            value={formData.subAssistants[0] || ''}
            onChange={(value) => handleFieldChange('subAssistants', [value])}
            placeholder="Select sub-assistants"
          />

          <div className={styles.divider} />

          {/* Attached Skills */}
          <div className={styles.skillsSection}>
            <label className={styles.label}>
              <Sparkles size={16} />
              Attached Skills
              <span className={styles.infoIcon}>ⓘ</span>
            </label>
            <p className={styles.helperText}>
              Select reusable skills to enhance this assistant's capabilities
            </p>
            <SkillSelector
              selectedSkillIds={formData.selectedSkills || []}
              onSelectionChange={(skillIds) => handleFieldChange('selectedSkills', skillIds)}
            />
          </div>
        </FormSection>

        {/* Tools & Integrations */}
        <FormSection title="Tools & Integrations">
          {/* Integration Apps - Clean Grid */}
          <div className={styles.integrationAppsSection}>
            <label className={styles.label}>
              Connected Apps
              <span className={styles.infoIcon}>ⓘ</span>
            </label>
            <p className={styles.helperText}>
              Click to connect your apps. Connected apps will show a green checkmark.
            </p>
            <IntegrationApps
              selectedApps={formData.mcpServers}
              onAppSelect={(appId, selected) => {
                setFormData(prev => ({
                  ...prev,
                  mcpServers: selected 
                    ? [...prev.mcpServers, appId]
                    : prev.mcpServers.filter(id => id !== appId),
                }));
              }}
              showConnectionStatus={true}
            />
          </div>

          <Accordion
            title="Available Tools"
            badge={getTotalSelectedTools() > 0 ? getTotalSelectedTools() : undefined}
            defaultExpanded={false}
          >
            <div className={styles.toolCategories}>
              {toolCategories.map((category) => (
                <ToolCategory
                  key={category.name}
                  name={category.name}
                  tools={category.tools}
                  selectedTools={formData.selectedTools[category.name] || []}
                  onToolChange={(toolId, checked) =>
                    handleToolChange(category.name, toolId, checked)
                  }
                  onSelectAll={(checked) =>
                    handleSelectAllTools(category.name, category.tools, checked)
                  }
                  onAddIntegration={() => handleAddIntegration(category.name)}
                />
              ))}
            </div>
          </Accordion>
        </FormSection>
      </div>

      {/* Integration Modal */}
      <IntegrationModal
        isOpen={integrationModal.isOpen}
        onClose={() => setIntegrationModal({ isOpen: false, categoryName: '' })}
        onSave={handleSaveIntegration}
        toolName={integrationModal.categoryName}
      />
    </form>
  );
};
